import { useEffect, useState, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

interface RomanProvincesLayerProps {
    show: boolean;
    selectedYear: number;
}

/**
 * Component to display Roman province boundaries
 * Shows administrative divisions of the Roman Empire with temporal filtering
 */
export function RomanProvincesLayer({ show, selectedYear }: RomanProvincesLayerProps) {
    const map = useMap();
    const [provincesLayer, setProvincesLayer] = useState<L.GeoJSON | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [allFeatures, setAllFeatures] = useState<unknown[]>([]);

    // Effect 1: Load the data only when show becomes true
    useEffect(() => {
        if (!show || allFeatures.length > 0 || isLoading) {
            return;
        }

        setIsLoading(true);

        // Fetch the GeoJSON file
        fetch('/roman-provinces.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                setAllFeatures(data.features || []);
                setIsLoaded(true);
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error loading Roman provinces:', error);
                setIsLoading(false);
            });
    }, [show, allFeatures.length, isLoading]);

    // Popup content generator
    const generatePopupContent = useCallback((feature: any) => {
        const { 
            name, 
            type,
            governor,
            capital,
            lowerDate,
            upperDate,
            description,
            significance
        } = feature.properties;
        
        // Format year for display
        const formatYear = (year: number | null | undefined) => {
            if (year === null || year === undefined || year === 0) return 'Unknown';
            return year < 0 ? `${Math.abs(year)} BC` : `${year} AD`;
        };
        
        const dateRange = `${formatYear(lowerDate)} - ${formatYear(upperDate)}`;
        
        return `
            <div class="space-y-2 text-sm max-w-[280px]">
                <h3 class="font-semibold text-base">${name}</h3>
                ${type ? `<p><strong>Type:</strong> ${type}</p>` : ''}
                <p><strong>Period:</strong> ${dateRange}</p>
                ${capital ? `<p><strong>Capital:</strong> ${capital}</p>` : ''}
                ${governor ? `<p><strong>Governor:</strong> ${governor}</p>` : ''}
                ${description ? `<p class="text-xs">${description}</p>` : ''}
                ${significance ? `
                    <div class="border-t border-gray-200 pt-2 mt-2">
                        <p class="font-semibold text-xs mb-1">Biblical Significance:</p>
                        <p class="text-xs">${significance}</p>
                    </div>
                ` : ''}
            </div>
        `;
    }, []);

    // Effect 2: Create layer once when data is loaded
    useEffect(() => {
        if (!show) {
            if (provincesLayer) {
                map.removeLayer(provincesLayer);
                setProvincesLayer(null);
            }
            return;
        }
        
        if (allFeatures.length === 0 || provincesLayer) {
            return;
        }

        const geojsonData = {
            type: 'FeatureCollection',
            features: allFeatures
        };

        // Create a custom pane for provinces to control z-index
        if (!map.getPane('provincesPane')) {
            const pane = map.createPane('provincesPane');
            pane.style.zIndex = '350'; // Below roads (400) but above empires (300)
        }

        // Province color scheme - earthy tones
        const provinceColors: Record<string, string> = {
            'Judea': '#8B7355',      // Brown
            'Syria': '#9B8B7E',      // Tan
            'Asia': '#7B9B8B',       // Sage green
            'Macedonia': '#8B7B9B',  // Purple-gray
            'Achaia': '#9B8B7B',     // Warm gray
            'Galatia': '#8B9B7B',    // Olive
            'Cilicia': '#7B8B9B',    // Blue-gray
            'Bithynia': '#9B7B8B',   // Mauve
            'default': '#A0826D'     // Default tan
        };

        // Create GeoJSON layer
        const layer = L.geoJSON(geojsonData as any, {
            pane: 'provincesPane',
            style: (feature) => {
                const provinceName = feature?.properties?.name || '';
                const color = provinceColors[provinceName] || provinceColors['default'];
                
                return {
                    color: color,
                    weight: 2,
                    opacity: 0.7,
                    fillColor: color,
                    fillOpacity: 0.2,
                    className: 'cursor-pointer'
                };
            },
            onEachFeature: (feature, layer) => {
                // Add hover effects
                layer.on({
                    mouseover: (e) => {
                        const l = e.target;
                        l.setStyle({
                            weight: 3,
                            fillOpacity: 0.35
                        });
                    },
                    mouseout: (e) => {
                        const l = e.target;
                        l.setStyle({
                            weight: 2,
                            fillOpacity: 0.2
                        });
                    }
                });
                
                // Add popup
                if (feature.properties) {
                    const popupContent = generatePopupContent(feature);
                    layer.bindPopup(popupContent, { maxWidth: 300 });
                }
            }
        });

        layer.addTo(map);
        setProvincesLayer(layer);
    }, [map, show, allFeatures, provincesLayer, generatePopupContent]);

    // Effect 3: Update visibility when selectedYear changes
    useEffect(() => {
        if (!provincesLayer || !show) {
            return;
        }

        // Filter provinces by timeline
        provincesLayer.eachLayer((layer: any) => {
            const feature = layer.feature;
            if (feature && feature.properties) {
                const lowerDate = feature.properties.lowerDate;
                const upperDate = feature.properties.upperDate;
                
                // Show if within date range
                const shouldShow = selectedYear >= lowerDate && selectedYear <= upperDate;
                
                if (shouldShow) {
                    if (!map.hasLayer(layer)) {
                        map.addLayer(layer);
                    }
                } else {
                    if (map.hasLayer(layer)) {
                        map.removeLayer(layer);
                    }
                }
            }
        });
    }, [selectedYear, provincesLayer, show, map]);

    return null;
}

